import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom"
import { ApolloProvider } from "@apollo/client/react"
import { client } from "@/graphql/client"
import { TranslationProvider } from "@/lib/i18n"
import { RootLayout } from "@/components/layout/root-layout"
import { KidLayout } from "@/components/kid/kid-layout"
import {
  AuthProvider,
  RequireAuth,
  RequireAdmin,
  RequireDevTools,
  RequireGuardian,
  useAuth,
} from "@/lib/auth/AuthContext"
import { useStickyStudentParam } from "@/lib/auth/use-sticky-student-param"

// Pages
import DashboardPage from "@/pages/DashboardPage"
import StudentsListPage from "@/pages/students/StudentsListPage"
import NewStudentPage from "@/pages/students/NewStudentPage"
import EditStudentPage from "@/pages/students/EditStudentPage"
import StudentDetailPage from "@/pages/students/StudentDetailPage"
import StudentSkillsPage from "@/pages/students/StudentSkillsPage"
import StudentIncorrectExercisesPage from "@/pages/students/StudentIncorrectExercisesPage"
import StudentAchievementsPage from "@/pages/students/StudentAchievementsPage"
import StudentProgressReportsPage from "@/pages/students/StudentProgressReportsPage"
import SessionsListPage from "@/pages/sessions/SessionsListPage"
import NewSessionPage from "@/pages/sessions/NewSessionPage"
import SessionDetailPage from "@/pages/sessions/SessionDetailPage"
import SolveSessionPage from "@/pages/sessions/SolveSessionPage"
import PrintSessionPage from "@/pages/sessions/PrintSessionPage"
import PrintTheoryPage from "@/pages/sessions/PrintTheoryPage"
import SubjectsListPage from "@/pages/subjects/SubjectsListPage"
import SubjectDetailPage from "@/pages/subjects/SubjectDetailPage"
import SettingsPage from "@/pages/settings/SettingsPage"
import BillingPage from "@/pages/BillingPage"
import KidHomePage from "@/pages/kid/KidHomePage"
import KidProfilePage from "@/pages/kid/KidProfilePage"
import KidProgressReportsPage from "@/pages/kid/KidProgressReportsPage"
import DecisionLabPage from "@/pages/lab/DecisionLabPage"
import SituationPage from "@/pages/lab/SituationPage"
import SituationSessionPage from "@/pages/lab/SituationSessionPage"
import PrintSituationPage from "@/pages/lab/PrintSituationPage"
import PrintSituationSessionPage from "@/pages/lab/PrintSituationSessionPage"
import AdminDashboardPage from "@/pages/admin/AdminDashboardPage"
import ExerciseBrowserPage from "@/pages/admin/ExerciseBrowserPage"
import ExerciseGeneratePage from "@/pages/admin/ExerciseGeneratePage"
import FlaggedContentPage from "@/pages/admin/FlaggedContentPage"
import TheoryGeneratePage from "@/pages/admin/TheoryGeneratePage"
import TheoryPreviewPage from "@/pages/admin/TheoryPreviewPage"
import PromptLayersPage from "@/pages/admin/PromptLayersPage"
import SvgShapesPage from "@/pages/admin/SvgShapesPage"
import LogsPage from "@/pages/admin/LogsPage"
import ErrorsPage from "@/pages/admin/ErrorsPage"
import AuditsPage from "@/pages/admin/AuditsPage"
import PerformancePage from "@/pages/admin/PerformancePage"
import AdminSupportPage from "@/pages/admin/SupportPage"
import SupportPage from "@/pages/SupportPage"
import UsersPage from "@/pages/admin/UsersPage"
import FeatureFlagsPage from "@/pages/admin/FeatureFlagsPage"
import LogDetailPage from "@/pages/admin/LogDetailPage"
import LoginPage from "@/pages/auth/LoginPage"
import RegisterPage from "@/pages/auth/RegisterPage"
import ForgotPasswordPage from "@/pages/auth/ForgotPasswordPage"
import ResetPasswordPage from "@/pages/auth/ResetPasswordPage"
import LandingPage from "@/pages/LandingPage"
import FaqPage from "@/pages/FaqPage"
import TermsPage from "@/pages/legal/TermsPage"
import PrivacyPage from "@/pages/legal/PrivacyPage"
import FamilyPage from "@/pages/family/FamilyPage"
import AcceptInvitationPage from "@/pages/family/AcceptInvitationPage"
import { usePendingInvitation } from "@/hooks/use-pending-invitation"

function FullScreenSpinner() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-primary" />
    </div>
  )
}

/**
 * The learner's route tree. Only these paths exist for a "student" role —
 * anything else lands on the kid home rather than flashing a grown-up page.
 * Typing a URL gets you nowhere, which is the point: the boundary is not a
 * hidden menu item.
 */
function KidRoutes() {
  return (
    <Routes>
      <Route element={<KidLayout />}>
        <Route path="/" element={<KidHomePage />} />
        <Route path="/profile" element={<KidProfilePage />} />
        {/* Same page a grown-up sees, minus nothing — it was already read-only. */}
        <Route path="/profile/achievements" element={<StudentAchievementsPage />} />
        {/* Their own progress reports — and, until one is available, how much further. */}
        <Route path="/profile/reports" element={<KidProgressReportsPage />} />
        <Route path="/sessions/:id/solve" element={<SolveSessionPage />} />
        {/* Sprendimų laboratorija. Reachable whether or not the flag is on —
            the page itself answers "neprieinama", because a route that does not
            exist bounces to the home page and looks like a broken link. */}
        <Route path="/lab" element={<DecisionLabPage />} />
        {/* A session sits above `:key` so „session" is never read as a
            problem key. */}
        <Route path="/lab/session/:id" element={<SituationSessionPage />} />
        <Route path="/lab/:key" element={<SituationPage />} />
      </Route>
      {/* Reachable by a learner too: they agreed to these when registering
          themselves, and a link that bounced them home would read as broken. */}
      <Route path="/taisykles" element={<TermsPage />} />
      <Route path="/privatumas" element={<PrivacyPage />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}

/**
 * `/` for everyone who is not a learner. A visitor gets the public page rather
 * than a bounce to the login card; a signed-in grown-up gets the dashboard,
 * wrapped in the same shell as every other grown-up route (RootLayout renders
 * the Outlet, which resolves to the index route below it).
 */
function RootRoute() {
  const { authenticated, loading } = useAuth()

  if (loading && !authenticated) return <FullScreenSpinner />
  if (!authenticated) return <LandingPage />

  return (
    <RequireGuardian>
      <RootLayout />
    </RequireGuardian>
  )
}

/** Everything a parent, teacher or admin can reach. */
function GrownUpRoutes() {
  return (
    <Routes>
      {/* Auth routes (no sidebar layout) */}
      <Route path="/auth/login" element={<LoginPage />} />
      <Route path="/auth/register" element={<RegisterPage />} />
      {/* Getting back in. Outside RequireGuardian and outside the sidebar for the
          reason the invitation page is: somebody who cannot sign in must not be
          bounced to the card they cannot get past. `/confirm/:token` is not here
          — it is a Rails route that redirects, since it has nothing to ask. */}
      <Route path="/auth/forgot" element={<ForgotPasswordPage />} />
      <Route path="/auth/reset/:token" element={<ResetPasswordPage />} />
      {/* A family invitation, opened from an email. Deliberately outside
          RequireGuardian and outside the sidebar shell: the person following it
          may have no account here at all, and a bounce to the login card would
          ask them to authenticate into a thing nobody has named. */}
      <Route path="/invitations/:token" element={<AcceptInvitationPage />} />
      {/* „Dažni klausimai" — PUBLIC, and outside the sidebar shell. It was a fold
          inside the support panel, which put the one page that can answer
          somebody locked out of the app behind signing in. It is also what the
          404 links to now, because /pagalba bounces a stranger to a login card. */}
      <Route path="/duk" element={<FaqPage />} />
      {/* Taisyklės and privatumo politika — PUBLIC, for the reason /duk is:
          they are read before an account exists, from the registration form. */}
      <Route path="/taisykles" element={<TermsPage />} />
      <Route path="/privatumas" element={<PrivacyPage />} />
      <Route
        path="/lab/:key/print"
        element={
          <RequireGuardian>
            <PrintSituationPage />
          </RequireGuardian>
        }
      />
      {/* A whole session on paper. Guardian-only, as the single-sheet print is:
          the options include the notes naming what each situation tests. */}
      <Route
        path="/lab/session/:id/print"
        element={
          <RequireGuardian>
            <PrintSituationSessionPage />
          </RequireGuardian>
        }
      />
      <Route
        path="/sessions/:id/print"
        element={
          <RequireAuth>
            <PrintSessionPage />
          </RequireAuth>
        }
      />
      {/* The reading, not the work: theory as its own sheet, one topic per page.
          `?skill=` is the one topic opened from a question. Outside the sidebar
          shell for the reason the worksheet is — this page exists to be a
          document. */}
      <Route
        path="/sessions/:id/theory/print"
        element={
          <RequireAuth>
            <PrintTheoryPage />
          </RequireAuth>
        }
      />

      {/* The root is the one path a stranger can arrive at, so it is the one that
          does not redirect to the login card: signed out it is the public page,
          signed in it is the dashboard in the usual shell. Everything else below
          still bounces to /auth/login. */}
      <Route path="/" element={<RootRoute />}>
        <Route index element={<DashboardPage />} />
      </Route>

      {/* Main app routes with sidebar layout (require a grown-up) */}
      <Route
        element={
          <RequireGuardian>
            <RootLayout />
          </RequireGuardian>
        }
      >
        {/* Help. „Parašyk mums" — the canonical page; the modal mounts the
            same component without leaving the screen. */}
        <Route path="/pagalba" element={<SupportPage />} />

        {/* Students */}
        <Route path="/students" element={<StudentsListPage />} />
        <Route path="/students/new" element={<NewStudentPage />} />
        <Route path="/students/:id" element={<StudentDetailPage />} />
        <Route path="/students/:id/edit" element={<EditStudentPage />} />
        <Route path="/students/:id/skills" element={<StudentSkillsPage />} />
        <Route path="/students/:id/incorrect" element={<StudentIncorrectExercisesPage />} />
        <Route path="/students/:id/achievements" element={<StudentAchievementsPage />} />
        <Route path="/students/:id/reports" element={<StudentProgressReportsPage />} />

        {/* Sessions */}
        <Route path="/sessions" element={<SessionsListPage />} />
        <Route path="/sessions/new" element={<NewSessionPage />} />
        <Route path="/sessions/:id" element={<SessionDetailPage />} />
        <Route path="/sessions/:id/grade" element={<SessionDetailPage />} />
        <Route path="/sessions/:id/solve" element={<SolveSessionPage />} />

        {/* Subjects */}
        <Route path="/subjects" element={<SubjectsListPage />} />
        <Route path="/subjects/:id" element={<SubjectDetailPage />} />

        {/* Sprendimų laboratorija */}
        <Route path="/lab" element={<DecisionLabPage />} />
        {/* A session sits above `:key` so „session" is never read as a
            problem key. */}
        <Route path="/lab/session/:id" element={<SituationSessionPage />} />
        <Route path="/lab/:key" element={<SituationPage />} />

        {/* Settings */}
        <Route path="/family" element={<FamilyPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/billing" element={<BillingPage />} />

        {/* Admin (require admin role) */}
        <Route path="/admin" element={<RequireAdmin><AdminDashboardPage /></RequireAdmin>} />
        {/* The content workbench: admin AND development only. In production these
            read a container's throwaway copy of what is already in git — see
            `DevTools`, which gates their resolvers with the same predicate. */}
        <Route path="/admin/exercises" element={<RequireDevTools><ExerciseBrowserPage /></RequireDevTools>} />
        <Route path="/admin/exercises/generate" element={<RequireAdmin><ExerciseGeneratePage /></RequireAdmin>} />
        <Route path="/admin/flagged" element={<RequireAdmin><FlaggedContentPage /></RequireAdmin>} />
        <Route path="/admin/support" element={<RequireAdmin><AdminSupportPage /></RequireAdmin>} />
        <Route path="/admin/theory" element={<RequireDevTools><TheoryPreviewPage /></RequireDevTools>} />
        <Route path="/admin/theory/generate" element={<RequireAdmin><TheoryGeneratePage /></RequireAdmin>} />
        <Route path="/admin/prompt-layers" element={<RequireDevTools><PromptLayersPage /></RequireDevTools>} />
        <Route path="/admin/audits" element={<RequireDevTools><AuditsPage /></RequireDevTools>} />
        <Route path="/admin/shapes" element={<RequireAdmin><SvgShapesPage /></RequireAdmin>} />
        <Route path="/admin/users" element={<RequireAdmin><UsersPage /></RequireAdmin>} />
        <Route path="/admin/features" element={<RequireAdmin><FeatureFlagsPage /></RequireAdmin>} />
        <Route path="/admin/logs" element={<RequireAdmin><LogsPage /></RequireAdmin>} />
        <Route path="/admin/logs/:id" element={<RequireAdmin><LogDetailPage /></RequireAdmin>} />
        <Route path="/admin/errors" element={<RequireAdmin><ErrorsPage /></RequireAdmin>} />
        <Route path="/admin/performance" element={<RequireAdmin><PerformancePage /></RequireAdmin>} />
      </Route>
    </Routes>
  )
}

/**
 * Which shell the app is. Decided by role rather than by a mode flag, so a
 * learner signing in with their own account and a learner handed the device by a
 * parent land in exactly the same place.
 */
function ShellRouter() {
  const { authenticated, role, loading } = useAuth()

  // Every learner URL carries `?as=<id>`, so any of them can be shared or
  // bookmarked to come straight back into this account.
  useStickyStudentParam()

  // A family invitation opened while signed out sends its recipient to Google,
  // which returns to `/` and forgets what they came for. This carries them the
  // rest of the way.
  usePendingInvitation()

  if (loading && !authenticated) return <FullScreenSpinner />
  if (authenticated && role === "student") return <KidRoutes />

  return <GrownUpRoutes />
}

function App() {
  return (
    <ApolloProvider client={client}>
      <TranslationProvider>
        <BrowserRouter>
          <AuthProvider>
            <ShellRouter />
          </AuthProvider>
        </BrowserRouter>
      </TranslationProvider>
    </ApolloProvider>
  )
}

export default App
